import { FormProps } from '@deltek/specification-client/core/Models/formProps'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { makeStyles, Theme } from '@material-ui/core/styles'
import TextField from '@material-ui/core/TextField'
import React from 'react'
import ReactResizeDetector from 'react-resize-detector'
import { ProductListingDetailFormField } from '../ListingDetailsPage'

const useStyles = makeStyles((theme: Theme) => ({
    container: { paddingTop: theme.spacing(2) },
    inputRoot: {
        borderRadius: 4,
    },
    font: {
        ...theme.typography.h2,
    },
    labelFocused: {
        fontSize: pxToRem(16),
    },
    invisibleDiv: {
        width: 'auto',
        display: 'inline-block',
        visibility: 'hidden',
        position: 'fixed',
        overflow: 'auto',
    },
}))

interface ModelEditProps extends FormProps {
    handleFieldChange: (
        field: ProductListingDetailFormField,
        value: string
    ) => void
}

export const EditModel: React.FC<ModelEditProps> = ({
    value,
    valid,
    required,
    handleFieldChange,
}) => {
    const classes = useStyles()

    const [localValue, setLocalValue] = React.useState<string>(value ?? '')
    const [hiddenDivWidth, setHiddenDivWidth] = React.useState<number>(0)
    const [fullWidth, setFullWidth] = React.useState<boolean>(false)
    const [focused, setFocused] = React.useState<boolean>(false)
    const containerRef = React.useRef<HTMLDivElement>(null)

    const debouncedInput = React.useCallback(
        (value: string) => {
            setLocalValue(value)
            handleFieldChange('model', value)
        },
        [handleFieldChange]
    )

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        debouncedInput(event.target.value)
    }

    return (
        <div className={classes.container} ref={containerRef}>
            <TextField
                id="model"
                data-testid="dvModel"
                name="model"
                style={{
                    width: fullWidth
                        ? containerRef?.current?.getBoundingClientRect().width
                        : 244,
                }}
                variant="outlined"
                required={required}
                label="Model #"
                placeholder="Model #"
                margin="dense"
                value={localValue}
                onChange={handleChange}
                error={!valid}
                InputProps={{
                    classes: {
                        input: classes.font,
                        root: classes.inputRoot,
                    },
                    onFocus: () => {
                        setFullWidth(true)
                        setFocused(true)
                    },
                    onBlur: () => {
                        setFullWidth(false)
                        setFocused(false)
                    },
                    onMouseOver: () => {
                        if (!focused) {
                            if (hiddenDivWidth > 137) {
                                setFullWidth(true)
                            }
                        }
                    },
                    onMouseOut: () => {
                        if (!focused && fullWidth) {
                            setFullWidth(false)
                        }
                    },
                }}
                inputProps={{
                    maxLength: 60,
                }}
                InputLabelProps={{
                    classes: {
                        root: classes.font,
                        shrink: classes.labelFocused,
                    },
                }}
                autoComplete="off"
            />
            <ReactResizeDetector
                handleWidth
                onResize={(width: number) => setHiddenDivWidth(width)}
            >
                {() => <div className={classes.invisibleDiv}>{localValue}</div>}
            </ReactResizeDetector>
        </div>
    )
}
